import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { fetchDashbaordData } from "@actions/fetchDashbaordDataAction";
import {
  formatDate_DD_MM_YYYY,
  getOneMonthBack,
  formatAmount,
} from "@utils/index";
import { useAppConfig } from "@context/AppConfigContext";
import { lobDashboardSections } from "./homeDataConstants";

const ApiResponseToLOBFormat = (apiData) => {
  return {
    atpOperations: {
      registrationsAndPolicies: {
        totalRegistrations: {
          count: apiData.totalRegistrations || 0,
        },
        totalPolicyCount: {
          count: apiData.totalRegistrations || 0,
        },
        activePolicy: {
          count: apiData.totalActivePolicies || 0,
        },
      },
      claims: {
        totalClaims: {
          count: apiData.totalClaims || 0,
        },
        activeClaims: {
          count: apiData.totalActiveClaims || 0,
        },
        approvedClaims: {
          count: apiData.totalApprovedClaims || 0,
        },
        rejectedClaims: {
          count: apiData.totalRejectedClaims || 0,
        },
      },
      financialOverview: {
        cdBalance: {
          count: formatAmount(Math.round(apiData.totalCDbalance || 0)),
        },
        totalClaimAmount: {
          count: formatAmount(Math.round(apiData.totalClaimAmount || 0)),
        },
        totalRedeemAmount: {
          count: formatAmount(Math.round(apiData.totalRedeemAmount || 0)),
        },
        cdBalancePremium: {
          count: formatAmount(Math.round(apiData.totalCDbalancePremium || 0)),
        },
      },
    },
  };
};

/**
 * API service functions
 */
export const getDashboardData = async (requestParams) => {
  try {
    const response = await fetchDashbaordData(requestParams);
    return {
      success: true,
      data: ApiResponseToLOBFormat(response.data),
      error: null,
    };
  } catch (err) {
    return {
      success: false,
      data: null,
      error: err.message || "Failed to fetch dashboard data",
    };
  }
};

// Comprehensive Home page hook that combines all logic
export const useHomePage = (user) => {
  const getInitialGreetingState = () => {
    try {
      const dismissed = sessionStorage.getItem("atp-greeting-dismissed");
      return dismissed !== "true";
    } catch (error) {
      return true;
    }
  };

  const getInitialWarningState = () => {
    try {
      const dismissed = sessionStorage.getItem("atp-warning-dismissed");
      return dismissed !== "true";
    } catch (error) {
      return true;
    }
  };

  const [fromDate, setFromDate] = useState(getOneMonthBack(new Date()));
  const [toDate, setToDate] = useState(new Date());
  const [showGreeting, setShowGreeting] = useState(getInitialGreetingState);
  const [showWarning, setShowWarning] = useState(getInitialWarningState);
  const [isClosing, setIsClosing] = useState(false);
  const [isWarningClosing, setIsWarningClosing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState([]);

  const { selectedClient } = useAppConfig();
  const navigate = useNavigate();

  const loadDashbaordData = useCallback(
    async (fromDate = null, toDate = null) => {
      setIsLoading(true);

      //  params
      const requestBody = {
        dateFilter: "CUSTOM",
        userType: user?.user?.role || user?.role, // user role type here
        clientName: selectedClient?.clientName,
      };

      if (fromDate && toDate) {
        // Format dates to DD-MM-YYYY format as required

        requestBody.fromDate = formatDate_DD_MM_YYYY(fromDate);
        requestBody.toDate = formatDate_DD_MM_YYYY(toDate);
      } else {
        const today = new Date();
        const oneMonthBack = getOneMonthBack(today);

        requestBody.fromDate = formatDate_DD_MM_YYYY(oneMonthBack);
        requestBody.toDate = formatDate_DD_MM_YYYY(today);
      }
      const result = await getDashboardData(requestBody);
      if (result.success) {
        const dashboardWithData = {
          ...lobDashboardSections,
          apiData: result.data, // Store the API data
        };

        setDashboardData(dashboardWithData);
      }

      setIsLoading(false);
    },
    [user],
  );

  // Initial data load
  useEffect(() => {
    loadDashbaordData(); // Load all data initially
  }, [loadDashbaordData]);

  const handleCloseAlertBanner = (bannerName = "atp-warning-dismissed") => {
    if (bannerName === "atp-warning-dismissed") {
      setIsWarningClosing(true);
    } else {
      setShowGreeting(true);
    }

    sessionStorage.setItem(bannerName, "true");

    setTimeout(() => {
      setShowGreeting(false);
      setShowWarning(false);
      setIsWarningClosing(false);
      setIsClosing(false);
    }, 300);
  };

  useEffect(() => {
    if (showGreeting && !isClosing) {
      const timer = setTimeout(() => {
        handleCloseAlertBanner("atp-greeting-dismissed");
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [showGreeting, isClosing]);

  const handleDateChange = (from, to) => {
    setFromDate(from);
    setToDate(to);

    // Validate date range
    if (from && to && from > to) {
      console.error("Invalid date range: from date cannot be after to date");
      return;
    }

    // Reload dashboard data with date filters (or without if cleared)
    loadDashbaordData(from, to);
  };

  const handleCardNavigation = (card) => {
    if (card.navigateTo) {
      navigate(card.navigateTo);
    }
  };

  // cd balance data
  const cdBalanceValue =
    dashboardData?.apiData?.atpOperations?.financialOverview?.cdBalance?.count;

  const parsedCdBalance = cdBalanceValue
    ? parseFloat(String(cdBalanceValue).replace(/[₹,\s]/g, ""))
    : null;

  // Check if balance is below
  const isBalanceLow = parsedCdBalance !== null && parsedCdBalance < 10000;

  return {
    showGreeting,
    showWarning,
    isClosing,
    isWarningClosing,
    fromDate,
    toDate,
    handleDateChange,
    isLoading,
    // error,
    dashboardData,
    isBalanceLow,
    handleCardNavigation,
    handleCloseAlertBanner,
  };
};
